import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';

export const LANGUAGES = ['ka', 'ru', 'en'] as const;
export type Language = (typeof LANGUAGES)[number];

export const LANGUAGE_LABELS: Record<Language, string> = {
  ka: 'ქართული', ru: 'Русский', en: 'English',
};

const STRINGS = {
  'app.name': { ka: 'AUDITORIA PRIVILEGE', ru: 'AUDITORIA PRIVILEGE', en: 'AUDITORIA PRIVILEGE' },

  'login.title': { ka: 'შესვლა', ru: 'Вход', en: 'Sign in' },
  'login.subtitle': {
    ka: 'შეიყვანეთ ტელეფონის ნომერი კოდის მისაღებად',
    ru: 'Введите номер телефона, чтобы получить код',
    en: 'Enter your phone number to receive a code',
  },
  'login.phone': { ka: 'ტელეფონი', ru: 'Телефон', en: 'Phone' },
  'login.send': { ka: 'კოდის გაგზავნა', ru: 'Отправить код', en: 'Send code' },
  'login.code': { ka: 'კოდი', ru: 'Код', en: 'Code' },
  'login.verify': { ka: 'შესვლა', ru: 'Войти', en: 'Sign in' },
  'login.back': { ka: 'უკან', ru: 'Назад', en: 'Back' },
  'login.sent': {
    ka: 'თუ ნომერი რეგისტრირებულია, შეტყობინება მოვა.',
    ru: 'Если номер зарегистрирован, сообщение придёт.',
    en: 'If the number is registered, the message will arrive.',
  },
  'login.noOtp': {
    ka: 'შესვლა ხდება სტუდიაში გაცემული ბმულით. მოგვმართეთ ადმინისტრატორს.',
    ru: 'Вход — по ссылке, которую выдаёт администратор в студии.',
    en: 'Sign in with a link our staff issue in the studio.',
  },
  'login.linkInvalid': {
    ka: 'ბმული აღარ მოქმედებს. მოითხოვეთ ახალი.',
    ru: 'Ссылка больше не действует. Попросите новую.',
    en: 'That link is no longer valid. Ask for a new one.',
  },
  'login.cardInvalid': {
    ka: 'ეს ბარათი ანგარიშს აღარ უკავშირდება.',
    ru: 'Эта карта больше не связана с аккаунтом.',
    en: 'That card is no longer linked to an account.',
  },
  'login.linkWorking': { ka: 'შესვლა...', ru: 'Входим...', en: 'Signing you in…' },

  'nav.card': { ka: 'ბარათი', ru: 'Карта', en: 'Card' },
  'nav.history': { ka: 'ისტორია', ru: 'История', en: 'History' },
  'nav.garage': { ka: 'ავტომობილები', ru: 'Гараж', en: 'Garage' },
  'nav.rewards': { ka: 'ჯილდოები', ru: 'Награды', en: 'Rewards' },
  'nav.profile': { ka: 'პროფილი', ru: 'Профиль', en: 'Profile' },

  'rewards.title': { ka: 'ჯილდოები', ru: 'Награды', en: 'Rewards' },
  'rewards.balance': { ka: 'თქვენი ქულები', ru: 'Ваши баллы', en: 'Your points' },
  'rewards.redeem': { ka: 'გაცვლა', ru: 'Обменять', en: 'Redeem' },
  'rewards.short': { ka: 'აკლია', ru: 'не хватает', en: 'short by' },
  'rewards.gone': { ka: 'აღარ არის ხელმისაწვდომი', ru: 'Больше недоступно', en: 'No longer available' },
  'rewards.empty': {
    ka: 'ამჟამად ჯილდოები არ არის.',
    ru: 'Сейчас наград нет.',
    en: 'No rewards right now.',
  },
  'rewards.mine': { ka: 'ჩემი კოდები', ru: 'Мои коды', en: 'My codes' },
  'rewards.past': { ka: 'გამოყენებული', ru: 'Использованные', en: 'Past rewards' },
  'rewards.showCode': {
    ka: 'აჩვენეთ ეს კოდი სტუდიაში',
    ru: 'Покажите этот код в студии',
    en: 'Show this code in the studio',
  },
  'rewards.used': { ka: 'გამოყენებული', ru: 'Использован', en: 'Used' },
  'rewards.expired': { ka: 'ვადაგასული', ru: 'Истёк', en: 'Expired' },
  'rewards.expires': { ka: 'მოქმედებს', ru: 'действует до', en: 'valid until' },
  'rewards.confirm': {
    ka: 'გავცვალოთ ქულები ამ ჯილდოზე?',
    ru: 'Обменять баллы на эту награду?',
    en: 'Exchange your points for this reward?',
  },
  'rewards.confirmYes': { ka: 'დიახ, გაცვლა', ru: 'Да, обменять', en: 'Yes, redeem' },
  'rewards.cancel': { ka: 'გაუქმება', ru: 'Отмена', en: 'Cancel' },

  'card.cashback': { ka: 'ქეშბექი', ru: 'Кэшбэк', en: 'Cashback' },
  'card.points': { ka: 'ქულები', ru: 'Баллы', en: 'Points' },
  'card.lifetime': { ka: 'სულ დახარჯული', ru: 'Всего потрачено', en: 'Lifetime spend' },
  'card.visits': { ka: 'ვიზიტები', ru: 'Визиты', en: 'Visits' },
  'card.pending': { ka: 'მომწიფებადი', ru: 'Ожидает', en: 'Maturing' },
  'card.showQr': { ka: 'აჩვენეთ სტუდიაში', ru: 'Покажите в студии', en: 'Show this in the studio' },
  'card.qrRefresh': { ka: 'კოდი განახლდება ავტომატურად', ru: 'Код обновляется автоматически', en: 'The code refreshes itself' },
  'ledger.visits': { ka: 'ვიზიტები', ru: 'Визиты', en: 'Visits' },
  'ledger.cashback': { ka: 'ქეშბექი', ru: 'Кэшбэк', en: 'Cashback' },
  'ledger.points': { ka: 'ქულები', ru: 'Баллы', en: 'Points' },
  'ledger.empty': {
    ka: 'ჯერ არაფერია.',
    ru: 'Пока ничего нет.',
    en: 'Nothing here yet.',
  },
  'ledger.EARN': { ka: 'დარიცხვა', ru: 'Начислено', en: 'Earned' },
  'ledger.REDEEM': { ka: 'გამოყენება', ru: 'Списано', en: 'Used' },
  'ledger.ADJUSTMENT': { ka: 'კორექტირება', ru: 'Корректировка', en: 'Adjustment' },
  'ledger.REVERSAL': { ka: 'გაუქმება', ru: 'Отмена', en: 'Reversed' },
  'ledger.REFUND_RETURN': { ka: 'დაბრუნებულია', ru: 'Возвращено', en: 'Returned' },
  'ledger.PENDING_RELEASE': { ka: 'გახდა ხელმისაწვდომი', ru: 'Стало доступно', en: 'Became available' },
  'ledger.EXPIRATION': { ka: 'ვადა გავიდა', ru: 'Сгорело', en: 'Expired' },
  'ledger.MERGE_TRANSFER': { ka: 'გადმოტანილია', ru: 'Перенесено', en: 'Transferred' },
  'referral.title': { ka: 'მოიწვიეთ მეგობარი', ru: 'Пригласите друга', en: 'Invite a friend' },
  'referral.hint': {
    ka: 'მიეცით ეს კოდი მეგობარს. მისი პირველი ვიზიტის შემდეგ ბონუსს ორივე მიიღებთ.',
    ru: 'Дайте этот код другу. После его первого визита бонус получите оба.',
    en: 'Give this code to a friend. After their first visit you both get a bonus.',
  },
  'referral.reward': { ka: 'თქვენი ბონუსი', ru: 'Ваш бонус', en: 'Your bonus' },
  'referral.joined': { ka: 'მოწვეული', ru: 'Приглашено', en: 'Invited' },
  'referral.rewarded': { ka: 'დარიცხული', ru: 'Начислено за', en: 'Paid out for' },
  'referral.copy': { ka: 'კოდის კოპირება', ru: 'Скопировать код', en: 'Copy the code' },
  'referral.copied': { ka: 'დაკოპირდა', ru: 'Скопировано', en: 'Copied' },
  'notif.referral.paid': {
    ka: 'თქვენი მოწვეული მეგობარი მოვიდა — ბონუსი დარიცხულია',
    ru: 'Ваш друг пришёл — бонус начислен',
    en: 'Your friend came in — your bonus has been added',
  },
  'notif.title': { ka: 'სიახლეები', ru: 'Новое', en: 'What’s new' },
  'notif.markRead': { ka: 'გასაგებია', ru: 'Понятно', en: 'Got it' },
  'notif.tier.upgraded': {
    ka: 'გილოცავთ, თქვენი დონეა {tierCode}',
    ru: 'Поздравляем, ваш уровень — {tierCode}',
    en: 'Congratulations — you are now {tierCode}',
  },
  'notif.reward.issued': {
    ka: 'ჯილდო მზადაა, კოდი {code}',
    ru: 'Награда готова, код {code}',
    en: 'Your reward is ready — code {code}',
  },
  'wallet.title': { ka: 'ბარათი ტელეფონში', ru: 'Карта в телефоне', en: 'Card on your phone' },
  'wallet.hint': {
    ka: 'ბალანსი თავად განახლდება ყოველი ვიზიტის შემდეგ.',
    ru: 'Баланс обновляется сам после каждого визита.',
    en: 'The balance updates itself after every visit.',
  },
  'wallet.apple': { ka: 'Apple Wallet-ში დამატება', ru: 'Добавить в Apple Wallet', en: 'Add to Apple Wallet' },
  'wallet.google': { ka: 'Google Wallet-ში დამატება', ru: 'Добавить в Google Wallet', en: 'Add to Google Wallet' },
  'wallet.added': { ka: 'დამატებულია', ru: 'Добавлена', en: 'Added' },
  'wallet.install': {
    ka: 'ტელეფონში დამატება',
    ru: 'Положить в телефон',
    en: 'Add it to your phone',
  },
  'wallet.preparing': { ka: 'მზადდება…', ru: 'Готовится…', en: 'Preparing…' },
  'wallet.prepare': {
    ka: 'ბარათის მომზადება',
    ru: 'Подготовить карту',
    en: 'Prepare my card',
  },
  // ---- ход работ по машине --------------------------------------------------
  'work.title': { ka: 'თქვენი მანქანა ჩვენთანაა', ru: 'Ваша машина у нас', en: 'Your car is with us' },
  'work.readyTitle': { ka: 'მანქანა მზადაა', ru: 'Машина готова', en: 'Your car is ready' },
  'work.yourCar': { ka: 'თქვენი მანქანა', ru: 'Ваша машина', en: 'Your car' },
  'work.waiting': { ka: 'რიგში', ru: 'Ждёт очереди', en: 'Waiting its turn' },
  'work.since': { ka: 'დაიწყო {time}', ru: 'начали в {time}', en: 'started at {time}' },
  'work.promised': { ka: 'მზად იქნება {time}', ru: 'обещали к {time}', en: 'promised by {time}' },
  'work.accepted': { ka: 'მიღებულია {time}', ru: 'приняли в {time}', en: 'taken in at {time}' },
  'work.doneOf': { ka: 'დასრულდა {done} / {total}', ru: 'сделано {done} из {total}', en: '{done} of {total} done' },
  'work.readyToCollect': { ka: 'შეგიძლიათ წაიყვანოთ', ru: 'Можно забирать', en: 'Ready to collect' },

  'card.toNextTier': { ka: 'შემდეგ დონემდე', ru: 'До следующего уровня', en: 'To the next tier' },
  'card.permanent': { ka: 'სამუდამო სტატუსი', ru: 'Постоянный статус', en: 'Permanent status' },
  'card.cashbackRate': { ka: 'ქეშბექი', ru: 'кэшбэк', en: 'cashback' },

  'history.title': { ka: 'ვიზიტების ისტორია', ru: 'История визитов', en: 'Visit history' },
  'history.empty': { ka: 'ვიზიტები ჯერ არ არის.', ru: 'Визитов пока нет.', en: 'No visits yet.' },
  'history.earned': { ka: 'დარიცხულია', ru: 'начислено', en: 'earned' },
  'history.used': { ka: 'გამოყენებულია', ru: 'использовано', en: 'used' },
  'history.refunded': { ka: 'დაბრუნებული', ru: 'Возврат', en: 'Refunded' },

  'garage.title': { ka: 'ჩემი ავტომობილები', ru: 'Мои автомобили', en: 'My vehicles' },
  'garage.empty': {
    ka: 'ავტომობილი ჯერ არ დაგიმატებიათ.',
    ru: 'Автомобили пока не добавлены.',
    en: 'No vehicles added yet.',
  },

  'profile.title': { ka: 'პროფილი', ru: 'Профиль', en: 'Profile' },
  'profile.language': { ka: 'ენა', ru: 'Язык', en: 'Language' },
  'profile.memberSince': { ka: 'ჩვენთან ერთად', ru: 'С нами с', en: 'Member since' },
  'profile.codes': { ka: 'ერთჯერადი კოდები', ru: 'Одноразовые коды', en: 'One-time codes' },
  'profile.codesOn': {
    ka: 'შესვლა ხდება SMS კოდით.',
    ru: 'Вход по SMS-коду.',
    en: 'You sign in with an SMS code.',
  },
  'profile.codesOff': {
    ka: 'თქვენ უარი თქვით კოდებზე. შესვლა ხდება ადმინისტრატორის ბმულით.',
    ru: 'Вы отказались от кодов. Вход — по ссылке от администратора.',
    en: 'You declined codes. You sign in with a link from our staff.',
  },
  'profile.declineCodes': { ka: 'კოდებზე უარის თქმა', ru: 'Отказаться от кодов', en: 'Decline codes' },
  'profile.enableCodes': { ka: 'კოდების ჩართვა', ru: 'Включить коды', en: 'Enable codes' },
  'profile.logout': { ka: 'გასვლა', ru: 'Выйти', en: 'Sign out' },

  'common.loading': { ka: 'იტვირთება...', ru: 'Загрузка...', en: 'Loading…' },
  'common.error': {
    ka: 'ვერ მოხერხდა. სცადეთ მოგვიანებით.',
    ru: 'Не удалось. Попробуйте позже.',
    en: 'Something went wrong. Try again later.',
  },
  'common.retry': { ka: 'ხელახლა', ru: 'Повторить', en: 'Retry' },
} as const;

export type StringKey = keyof typeof STRINGS;

const STORAGE_KEY = 'auditoria.language';

function detectLanguage(): Language {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored && LANGUAGES.includes(stored as Language)) return stored as Language;
  } catch {
    // private mode or blocked storage: fall through to the browser hint
  }
  const preferred = navigator.languages?.find((tag) =>
    LANGUAGES.includes(tag.slice(0, 2) as Language),
  );
  return (preferred?.slice(0, 2) as Language | undefined) ?? 'ka';
}

interface I18nValue {
  language: Language;
  setLanguage: (next: Language) => void;
  t: (key: StringKey, params?: Record<string, unknown>) => string;
}

const I18nContext = createContext<I18nValue | null>(null);

export function I18nProvider({ children }: { children: ReactNode }) {
  const [language, setLanguageState] = useState<Language>(detectLanguage);

  const setLanguage = useCallback((next: Language) => {
    setLanguageState(next);
    document.documentElement.lang = next;
    try {
      localStorage.setItem(STORAGE_KEY, next);
    } catch {
      // a preference we cannot persist is still worth honouring this session
    }
  }, []);

  const value = useMemo<I18nValue>(() => ({
    language,
    setLanguage,
    // `{name}` placeholders are filled from the caller's params. A value that
    // was not supplied is left as-is rather than printed as "undefined".
    t: (key, params) => {
      const text = STRINGS[key][language];
      if (!params) return text;
      return text.replace(/\{(\w+)\}/g, (whole, name: string) => {
        const value = params[name];
        return value === undefined || value === null ? whole : String(value);
      });
    },
  }), [language, setLanguage]);

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n(): I18nValue {
  const value = useContext(I18nContext);
  if (!value) throw new Error('useI18n used outside I18nProvider');
  return value;
}

export const LOCALES: Record<Language, string> = { ka: 'ka-GE', ru: 'ru-RU', en: 'en-GB' };
